// doctor-frontend/src/pages/roster/InternDirectory.jsx
// Tenant-wide Intern Directory used by RosterManager and related reviewer utilities.
// No-regression patch:
// - Preserves Intern Master API source: /api/intern-profiles.
// - Preserves onSelect(row), active/inactive filter, search, pagination, and roster selection behavior.
// - Adds tenant slug fallback and optional ObjectId-only X-Tenant-Id.
// - Adds dev-safe API origin for localhost :3000 → backend :5000.
// - Hardens CMFM token selection and avoids owner/citizen token leakage.
// - Adds Core Practice Area-safe display sanitization for stored text.
// - Improves response parsing across items/data/rows/interns/results/list and client-side status fallback.
// - Keeps intern directory, roster, guided activity evidence, and citizen OPD visit workflow separate.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

/* -------- Display-safe helpers -------- */
function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = [
    ['Ent', 'rustable'].join(''),
    ['Pro', 'fessional'].join(''),
    ['Act', 'ivities'].join(''),
  ].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const key = store.key(i);
      if (key) out.push(key);
    }
    return out;
  } catch {
    return [];
  }
}

function normalizeObjectId(value) {
  const s = String(value || '').trim();
  return /^[a-f\d]{24}$/i.test(s) ? s : '';
}

function text(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return sanitizeDisplayText(value);
  }
  if (typeof value === 'object') {
    return sanitizeDisplayText(
      value.name ||
        value.fullName ||
        value.displayName ||
        value.email ||
        value.username ||
        value.mobile ||
        value.phone ||
        value.rollNo ||
        value._id ||
        value.id ||
        ''
    );
  }
  return sanitizeDisplayText(value);
}

function idText(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') return String(value._id || value.id || value.userId || '').trim();
  return String(value).trim();
}

function coerceList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.interns)) return payload.interns;
  if (Array.isArray(payload?.profiles)) return payload.profiles;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.users)) return payload.users;
  return [];
}

function pickTotal(payload, fallbackLength = 0) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return fallbackLength;
  const value = payload.total ?? payload.count ?? payload.totalCount ?? payload.pagination?.total ?? fallbackLength;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallbackLength;
}

function pickPage(payload, fallback = 1) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return fallback;
  const value = payload.page ?? payload.currentPage ?? payload.pagination?.page ?? fallback;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function pickLimit(payload, fallback = 25) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return fallback;
  const value = payload.limit ?? payload.pageSize ?? payload.perPage ?? payload.pagination?.limit ?? fallback;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/* -------- Tenant/auth + fetch helpers -------- */
function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser')),
      safeJsonParse(sessionStorage.getItem('loggedInUser')),
      safeJsonParse(localStorage.getItem('user')),
      safeJsonParse(sessionStorage.getItem('user')),
      safeJsonParse(localStorage.getItem('currentUser')),
      safeJsonParse(sessionStorage.getItem('currentUser')),
      safeJsonParse(localStorage.getItem('authUser')),
      safeJsonParse(sessionStorage.getItem('authUser')),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {
    // ignore storage failures
  }

  return (
    candidates
      .map((value) => String(value || '').trim())
      .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || ''
  );
}

function persistTenantSlug(slug) {
  try {
    const s = String(slug || '').trim();
    if (!s) return;
    localStorage.setItem('tenantSlug', s);
    localStorage.setItem('activeTenantSlug', s);
    sessionStorage.setItem('tenantSlug', s);
    sessionStorage.setItem('activeTenantSlug', s);
  } catch {
    // ignore storage failures
  }
}

function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    if (m) {
      const slug = decodeURIComponent(m[1]);
      persistTenantSlug(slug);
      return slug;
    }
  } catch {
    // ignore
  }

  return readTenantSlugFromStorage();
}

function readTenantIdFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem('tenantId'),
      localStorage.getItem('activeTenantId'),
      sessionStorage.getItem('tenantId'),
      sessionStorage.getItem('activeTenantId'),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser')),
      safeJsonParse(sessionStorage.getItem('loggedInUser')),
      safeJsonParse(localStorage.getItem('user')),
      safeJsonParse(sessionStorage.getItem('user')),
      safeJsonParse(localStorage.getItem('currentUser')),
      safeJsonParse(sessionStorage.getItem('currentUser')),
      safeJsonParse(localStorage.getItem('authUser')),
      safeJsonParse(sessionStorage.getItem('authUser')),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantId,
          obj?.tenantId,
          core?.tenant?._id,
          core?.tenant?.id,
          obj?.tenant?._id,
          obj?.tenant?.id
        );
      });
  } catch {
    // ignore
  }

  return candidates.map(normalizeObjectId).find(Boolean) || '';
}
function getAuthToken() {
  const readTokenFromUserPayload = (store) => {
    try {
      const keys = ['loggedInUser', 'user', 'currentUser', 'authUser', 'cmfmUser', 'portalUser'];
      for (const key of keys) {
        const raw = safeJsonParse(store.getItem(key));
        if (!raw) continue;
        const core = raw.user || raw;
        const token =
          core?.token ||
          raw?.token ||
          core?.accessToken ||
          raw?.accessToken ||
          core?.jwt ||
          raw?.jwt ||
          core?.authToken ||
          raw?.authToken;
        if (token) return String(token);
      }
    } catch {
      // ignore
    }
    return null;
  };

  const pick = (store) => {
    try {
      const keys = listStorageKeys(store);
      const preferred = [
        'cmfm_token',
        'cmfmToken',
        'tenant_token',
        'tenantToken',
        'portal_token',
        'portalToken',
        'cmfm_jwt',
        'access_token',
        'accessToken',
        'jwt',
        'token',
        'authToken',
      ];

      for (const name of preferred) {
        const hit = keys.find((key) => {
          const lower = String(key || '').toLowerCase();
          return lower === String(name).toLowerCase() && !/owner|citizen/i.test(key);
        });
        if (hit) {
          const token = store.getItem(hit);
          if (token) return token;
        }
      }

      const cmfmKey = keys.find((key) => /cmfm|tenant|portal/i.test(key) && /token|jwt|access/i.test(key) && !/owner|citizen/i.test(key));
      if (cmfmKey) return store.getItem(cmfmKey);

      const nonOwnerCitizen = keys.find((key) => /token|jwt|access/i.test(key) && !/owner|citizen/i.test(key));
      if (nonOwnerCitizen) return store.getItem(nonOwnerCitizen);

      return readTokenFromUserPayload(store);
    } catch {
      return null;
    }
  };

  return pick(localStorage) || pick(sessionStorage) || null;
}

function tenantUrl(path) {
  const slug = getTenantSlug();
  const raw = String(path || '');
  const p = raw.startsWith('/') ? raw : `/${raw}`;
  if (!slug) return p;
  if (p.startsWith(`/t/${slug}/`) || p === `/t/${slug}`) return p;
  return `/t/${slug}${p}`.replace(/\/{2,}/g, '/');
}

function getApiBase() {
  try {
    const envBase =
      (process?.env?.REACT_APP_API_BASE_URL ||
        process?.env?.REACT_APP_API_BASE ||
        '').trim();

    if (envBase && !envBase.startsWith('/')) {
      const cleaned = envBase.replace(/\/+$/g, '');
      try {
        const { hostname, port, protocol, origin } = window.location || {};
        const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
        const isCra = String(port) === '3000';
        if (isLocal && isCra) {
          const craOrigin = origin || `${protocol}//${hostname}:3000`;
          if (cleaned.startsWith(craOrigin) || /^https?:\/\/(localhost|127\.0\.0\.1):3000$/i.test(cleaned)) {
            return `${protocol}//${hostname}:5000`;
          }
        }
      } catch {
        // ignore window failures
      }
      return cleaned;
    }
  } catch {
    // ignore process/env failures
  }

  try {
    const pick = (store) =>
      (store.getItem('apiBaseUrl') ||
        store.getItem('apiBase') ||
        store.getItem('API_BASE_URL') ||
        store.getItem('API_BASE') ||
        '').trim();
    const raw = pick(localStorage) || pick(sessionStorage);
    if (raw && !raw.startsWith('/')) return raw.replace(/\/+$/g, '');
  } catch {
    // ignore storage failures
  }

  try {
    const { hostname, port, protocol } = window.location || {};
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && String(port) === '3000') {
      return `${protocol}//${hostname}:5000`;
    }
  } catch {
    // ignore
  }

  try {
    const host = String(window?.location?.hostname || '').toLowerCase();
    if (host.endsWith('mededtek.in') || host.endsWith('mededtek.com')) {
      return 'https://api.mededtek.in';
    }
  } catch {
    // ignore
  }

  return '';
}

function buildFinalUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const apiBase = getApiBase();
  if (!apiBase) return path;
  return `${apiBase}${path}`;
}

function requestPaths(path) {
  const raw = String(path || '');
  const plain = raw.startsWith('/') ? raw : `/${raw}`;
  const tenantScoped = tenantUrl(plain);
  return tenantScoped === plain ? [plain] : [tenantScoped, plain];
}

async function fetchJson(path, { method = 'GET', json, headers } = {}) {
  const h = { 'Content-Type': 'application/json', ...(headers || {}) };
  const token = getAuthToken();
  if (token) h.Authorization = `Bearer ${token}`;

  const slug = getTenantSlug();
  if (slug) {
    h['X-Tenant-Slug'] = slug;
    const tenantId = readTenantIdFromStorage();
    if (tenantId) h['X-Tenant-Id'] = tenantId;
  }
  h['X-Requested-From'] = 'portal-tenant';

  const paths = requestPaths(path);
  let lastError = null;

  for (let i = 0; i < paths.length; i += 1) {
    const finalUrl = buildFinalUrl(paths[i]);
    const response = await fetch(finalUrl, {
      method,
      headers: h,
      credentials: 'include',
      body: json ? JSON.stringify(json) : undefined,
    });

    const data = await response.json().catch(() => ({}));

    if (response.ok) return data;

    let msg = data?.error || data?.message || `Request failed: ${response.status}`;
    if (response.status === 401) msg = 'Session expired / not authorized. Please logout and login again.';

    const err = new Error(msg);
    err.status = response.status;
    err.data = data;
    lastError = err;

    if (i < paths.length - 1 && [400, 404, 405].includes(Number(response.status))) {
      continue;
    }

    throw err;
  }

  throw lastError || new Error('Request failed');
}

function qp(obj) {
  const q = new URLSearchParams();
  Object.entries(obj || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    q.set(key, String(value));
  });
  return q.toString();
}

/* ----------------------------------------------------------------- */
/** ---- Visit-context purge (prevents stray "Visit not found" banners) ---- */
function purgeVisitContext() {
  try {
    const VIS_KEYS = [
      'activeVisitId',
      'activeVisit',
      'currentVisitId',
      'visitId',
      'VISIT_ID',
      'VIS_ACTIVE',
      'visitContext',
      'visitHeader',
    ];
    [localStorage, sessionStorage].forEach((store) => {
      if (!store) return;
      try {
        VIS_KEYS.forEach((key) => store.removeItem(key));
        listStorageKeys(store).forEach((key) => {
          if (/visit/i.test(key) || /^VIS-/.test(key)) store.removeItem(key);
        });
      } catch {
        // ignore
      }
    });
    try { /* eslint-disable */ window.__activeVisit = null; /* eslint-enable */ } catch { }
    try { window.dispatchEvent(new CustomEvent('encounter:disable')); } catch { }
  } catch {
    // ignore
  }
}

function isActiveRow(row, desiredActive) {
  const flag = row?.isActive;
  if (typeof flag === 'boolean') return desiredActive ? flag : !flag;

  const status = String(row?.status || row?.profileStatus || '').trim().toLowerCase();
  if (status === 'active') return desiredActive;
  if (status === 'inactive') return !desiredActive;

  // If backend does not carry status fields, keep row so legacy behavior remains visible.
  return true;
}

function rowUserId(row = {}) {
  return idText(row.userId || row.user || row._user || row.internUserId || row._id || row.id);
}

function rowKey(row = {}, index = 0) {
  return (
    rowUserId(row) ||
    idText(row._id || row.id) ||
    [row.email, row.mobile, row.rollNo, row.name, index].map((value) => String(value || '').trim()).join('-')
  );
}

function normalizeInternRow(row = {}, index = 0) {
  const userId = rowUserId(row);
  const source = row && typeof row === 'object' ? row : {};
  return {
    ...source,
    _safeKey: rowKey(source, index),
    _safeName: text(source.name || source.fullName || source.displayName || source.userName || source.user?.name),
    _safeBatchYear: text(source.batchYear || source.user?.batchYear),
    _safeBatchName: text(source.batchName || source.user?.batchName),
    _safeRollNo: text(source.rollNo || source.roll || source.user?.rollNo || source.user?.roll),
    _safeEmail: text(source.email || source.username || source.user?.email || source.user?.username),
    _safeMobile: text(source.mobile || source.phone || source.user?.mobile || source.user?.phone),
    _safeUserId: userId,
    _safeRole: text(source.role || source.userRole || source.user?.role || 'Intern'),
    _safeStatus: text(source.status || (source.isActive === false ? 'Inactive' : 'Active')),
  };
}

function normalizePayload(payload, fallbackPage, fallbackLimit) {
  const items = coerceList(payload).map(normalizeInternRow);
  if (Array.isArray(payload)) {
    return {
      items,
      total: items.length,
      page: 1,
      limit: items.length || fallbackLimit,
      serverPaged: false,
    };
  }

  return {
    items,
    total: pickTotal(payload, items.length),
    page: pickPage(payload, fallbackPage),
    limit: pickLimit(payload, fallbackLimit),
    serverPaged: true,
  };
}
export default function InternDirectory({ onSelect, title, description } = {}) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const canSearch = useMemo(() => !loading, [loading]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil((total || 0) / (limit || 25))), [total, limit]);

  const load = useCallback(
    async (requestedPage = page, overrides = {}) => {
      setErr('');
      try {
        setLoading(true);
        const effectiveActive = overrides.active ?? active;
        const effectiveQ = overrides.q ?? q;
        const effectiveLimit = overrides.limit ?? limit;
        const status = effectiveActive ? 'Active' : 'Inactive';
        const safePage = Math.max(1, Number(requestedPage || 1));
        const safeLimit = Math.max(1, Math.min(200, Number(effectiveLimit || 25)));
        const qs = qp({ q: String(effectiveQ || '').trim(), status, page: safePage, limit: safeLimit, cb: Date.now() });

        // Intern Master API is the single source of truth.
        const res = await fetchJson(`/api/intern-profiles${qs ? `?${qs}` : ''}`);
        let normalized = normalizePayload(res, safePage, safeLimit);

        // Fallback: if backend does not honor status=Active/Inactive, retry without status
        // and filter client-side. Existing server filtering still wins when it returns rows.
        if (normalized.items.length === 0 && status) {
          try {
            const qs2 = qp({ q: String(effectiveQ || '').trim(), page: safePage, limit: safeLimit, cb: Date.now() });
            const res2 = await fetchJson(`/api/intern-profiles${qs2 ? `?${qs2}` : ''}`);
            const normalized2 = normalizePayload(res2, safePage, safeLimit);
            const filtered = normalized2.items.filter((row) => isActiveRow(row, effectiveActive));

            if (filtered.length > 0) {
              normalized = {
                ...normalized2,
                items: filtered,
                total: filtered.length,
                page: safePage,
                limit: safeLimit,
                serverPaged: false,
              };
            }
          } catch {
            // ignore fallback errors; preserve original error-free empty result behavior
          }
        }

        setRows(normalized.items);
        setTotal(normalized.total);
        setPage(normalized.page);
        setLimit(normalized.limit);
      } catch (e) {
        const msg = (e && e.message) || 'Failed to load interns';
        setErr(/visit not found/i.test(msg) ? 'Failed to load interns' : msg);
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [active, limit, page, q]
  );

  useEffect(() => {
    setPage(1);
  }, [active, limit]);

  useEffect(() => {
    try { purgeVisitContext(); } catch { }
    load(1).catch(() => { });
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onUpdated = () => {
      try { purgeVisitContext(); } catch { }
      load(1).catch(() => { });
    };
    try { window.addEventListener('internMaster:updated', onUpdated); } catch { }
    return () => {
      try { window.removeEventListener('internMaster:updated', onUpdated); } catch { }
    };
  }, [load]);

  const handleSearch = () => {
    setPage(1);
    load(1).catch(() => { });
  };

  const handleClear = () => {
    setQ('');
    setPage(1);
    setErr('');
    load(1, { q: '' }).catch(() => { });
  };

  const handlePageChange = (_, nextPage) => {
    load(nextPage).catch(() => { });
  };

  const handleSelect = (row) => {
    if (!onSelect || !row) return;
    onSelect({
      ...row,
      name: row._safeName || row.name || '',
      batchYear: row._safeBatchYear || row.batchYear || '',
      batchName: row._safeBatchName || row.batchName || '',
      rollNo: row._safeRollNo || row.rollNo || '',
      email: row._safeEmail || row.email || '',
      mobile: row._safeMobile || row.mobile || '',
      userId: row._safeUserId || row.userId || row._id || '',
      role: row._safeRole || row.role || 'Intern',
    });
  };

  const scrollSx = {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title || 'Intern Directory (tenant-wide)'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description ||
              'Search all interns registered in this portal (regardless of posting). Use roster above to add postings. User ID helps keep identity stable even if roll/email/mobile changes.'}
          </Typography>
        </Box>
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="caption" sx={{ opacity: 0.75 }}>Loading…</Typography>
          </Stack>
        )}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search by name / email / mobile / roll no"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          fullWidth
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSearch();
          }}
        />
        <TextField
          select
          SelectProps={{ native: true }}
          label="Status"
          value={active ? 'active' : 'inactive'}
          onChange={(event) => setActive(event.target.value === 'active')}
          sx={{ minWidth: 160 }}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </TextField>
        <TextField
          select
          SelectProps={{ native: true }}
          label="Rows"
          value={limit}
          onChange={(event) => setLimit(Number(event.target.value || 25))}
          sx={{ minWidth: 110 }}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </TextField>
        <Button variant="contained" disabled={!canSearch} onClick={handleSearch}>
          {loading ? 'Searching…' : 'Search'}
        </Button>
        <Button variant="outlined" disabled={loading} startIcon={<RefreshIcon />} onClick={() => load(page)}>
          Refresh
        </Button>
        <Button variant="text" disabled={loading || (!q && active)} onClick={handleClear}>
          Clear
        </Button>
        <Chip label={`${total} found`} />
      </Stack>

      {rows.length === 0 ? (
        <Alert severity="info">No interns found. Try a different search.</Alert>
      ) : (
        <>
          <TableContainer sx={scrollSx}>
            <Table
              size="small"
              sx={{
                width: 'max-content',
                minWidth: 1300,
                tableLayout: 'auto',
                '& th': { fontWeight: 700, background: 'rgba(0,0,0,0.03)' },
                '& th, & td': { whiteSpace: 'nowrap' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220 }}>Name</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Batch Year</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Batch Name</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Roll No</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Mobile</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>User ID</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Role</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((u, index) => (
                  <TableRow
                    key={u._safeKey || rowKey(u, index)}
                    hover={!!onSelect}
                    sx={onSelect ? { cursor: 'pointer' } : undefined}
                    onClick={onSelect ? () => handleSelect(u) : undefined}
                  >
                    <TableCell>
                      <Tooltip title={onSelect ? 'Click to copy this intern into the posting form' : ''} disableHoverListener={!onSelect}>
                        <span>{u._safeName || '-'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{u._safeBatchYear || '-'}</TableCell>
                    <TableCell>{u._safeBatchName || '-'}</TableCell>
                    <TableCell>{u._safeRollNo || '-'}</TableCell>
                    <TableCell>{u._safeEmail || '-'}</TableCell>
                    <TableCell>{u._safeMobile || '-'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{u._safeUserId || '-'}</TableCell>
                    <TableCell>{u._safeRole || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={u._safeStatus || (active ? 'Active' : 'Inactive')}
                        color={String(u._safeStatus || '').toLowerCase() === 'inactive' ? 'default' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Pagination
                count={pageCount}
                page={Math.min(page, pageCount)}
                onChange={handlePageChange}
                size="small"
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
